import type {
  PrintIssue,
  PrintIssueCategory,
  PrintValidationResult,
} from "@schoolmag/contracts";
import {
  AlertTriangle,
  CheckCircle2,
  CircleX,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

const categoryNames: Record<PrintIssueCategory, string> = {
  content: "Content",
  image: "Images",
  bleed: "Bleed",
  safe_area: "Safe area",
  font: "Fonts",
  system: "Export",
};

export function groupPreflightIssues(
  issues: PrintIssue[],
): Array<{ category: PrintIssueCategory; issues: PrintIssue[] }> {
  const grouped = new Map<PrintIssueCategory, PrintIssue[]>();
  for (const issue of issues) {
    const category = issue.category ?? "system";
    grouped.set(category, [...(grouped.get(category) ?? []), issue]);
  }
  return [...grouped.entries()].map(([category, categoryIssues]) => ({
    category,
    issues: categoryIssues,
  }));
}

export function ExportPreflightResults({
  publicationId,
  result,
}: {
  publicationId: string;
  result: PrintValidationResult;
}) {
  const grouped = groupPreflightIssues(result.issues);
  return (
    <section
      className={`mt-3 overflow-hidden rounded-2xl border ${result.ready ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
      aria-live="polite"
    >
      <div className="flex items-start gap-3 p-4">
        {result.ready ? (
          <CheckCircle2
            className="mt-0.5 shrink-0 text-emerald-700"
            size={19}
          />
        ) : (
          <CircleX className="mt-0.5 shrink-0 text-red-700" size={19} />
        )}
        <div>
          <p
            className={`font-bold ${result.ready ? "text-emerald-900" : "text-red-900"}`}
          >
            {result.ready
              ? "Ready to export"
              : `${result.errors} blocker${result.errors === 1 ? "" : "s"} to fix`}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            {result.warnings} warning{result.warnings === 1 ? "" : "s"} ·{" "}
            {result.pageCount} pages checked
          </p>
        </div>
      </div>
      {grouped.length > 0 && (
        <div className="max-h-80 space-y-3 overflow-y-auto border-t border-black/5 bg-white/55 p-3">
          {grouped.map((group) => (
            <div key={group.category}>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <AlertTriangle size={11} />
                {categoryNames[group.category]} · {group.issues.length}
              </div>
              <div className="space-y-1.5">
                {group.issues.map((issue, index) => (
                  <div
                    className="rounded-lg border border-black/5 bg-white p-2.5 text-xs text-slate-700"
                    key={`${issue.code}-${issue.pageId ?? "publication"}-${issue.elementId ?? index}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-1 size-1.5 shrink-0 rounded-full ${issue.severity === "error" ? "bg-red-500" : "bg-amber-500"}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="leading-5">{issue.message}</p>
                        {issue.pageId && (
                          <Link
                            className="mt-1 inline-flex items-center gap-1 font-bold text-brand hover:underline"
                            to={`/app/publications/${publicationId}/pages/${issue.pageId}`}
                          >
                            Fix page {issue.pageNumber}
                            <ExternalLink size={11} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
